// HR count tiles for the main dashboard: attendance, onboarding, upcoming
// renewals and pending approvals. Data comes from the same thunks the HR
// Payroll dashboard uses (pages/HrPayrollDashboard/hooks/useDashboardData.js).
// Nothing is fetched and no tiles are returned when `enabled` is false.
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  fetchTodaySummary,
  fetchPendingRequests,
} from "../../AttendanceOverview/attendanceOverviewThunk";
import { loadAllNotifications } from "../../../store/commonThunks/notificationsThunk";
import { fetchOnboardings } from "../../Onboarding/onboardingThunks";
import { TILE } from "../dashboardTheme";

const useHrTiles = (enabled) => {
  const dispatch = useDispatch();
  const summary = useSelector((s) => s.attendanceOverview?.attendanceSummary);
  const renewals = useSelector((s) => s.notifications?.expiryListHR?.length || 0);
  const approvals = useSelector((s) => s.attendanceOverview?.pendingRequests?.pending?.length || 0);
  // Same count as the HR Payroll dashboard: onboardings not yet converted to employees.
  const onboarding = useSelector(
    (s) => (s.onboarding?.list || []).filter((o) => o.status !== "converted").length
  );

  useEffect(() => {
    if (!enabled) return;
    dispatch(fetchOnboardings());
    dispatch(fetchTodaySummary({ notIncludeTerminated: true }));
    dispatch(fetchPendingRequests());
    dispatch(loadAllNotifications({}));
  }, [dispatch, enabled]);

  if (!enabled) return [];

  return [
    {
      key: "attendance",
      title: "Attendance Overview",
      icon: <CalendarOutlined />,
      accent: TILE.attendance,
      value: summary?.present ?? 0,
      unit: `/ ${summary?.total_users ?? 0}`,
      sub: "present today",
      to: "/hrpayroll/attendance?tab=clockinout",
    },
    {
      key: "onboarding",
      title: "Onboarding",
      icon: <UserOutlined />,
      accent: TILE.onboarding,
      value: onboarding,
      sub: "in progress",
      to: "/hrpayroll/onboarding",
    },
    {
      key: "renewals",
      title: "Upcoming Renewals",
      icon: <ClockCircleOutlined />,
      accent: TILE.renewals,
      value: renewals,
      sub: "due in the next 30 days",
      to: "/hrpayroll/dashboard",
    },
    {
      key: "approvals",
      title: "Pending Approvals",
      icon: <CheckCircleOutlined />,
      accent: approvals > 0 ? TILE.approvals : TILE.calm,
      value: approvals,
      danger: approvals > 0,
      sub: "attendance requests",
      to: "/hrpayroll/attendance?approve",
    },
  ];
};

export default useHrTiles;
